import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, TriggerButton } from "widgets";
import { useResizeObserver } from "widgets/hooks";

import { useGenomeViewerStore } from "../../store/store.ts";
import { useGroupHeight } from "./hooks/use-group-height.ts";
import { TrackGroupOverview } from "./TrackGroupOverview.tsx";
import { getPanelHeight, getScale } from "./utils/panel.ts";
import { VisibleAreaBox } from "./VisibleAreaBox.tsx";
import { ZoomControl } from "./ZoomControl.tsx";

export const OverviewCard = () => {
  const [show, setShow] = useState(true);

  return (
    <Card
      noBorder
      title="Overview"
      actions={[
        <ZoomControl key={"zoom-field"} />,
        <TriggerButton
          key={"toggle-overview"}
          label="Hide"
          onPress={() => setShow((s) => !s)}
          icon={show ? "unfold_less_double" : "unfold_more_double"}
          // intent={show ? undefined : TriggerButtonIntent.Active}
        />,
      ]}
      style={{ flex: "0 0 auto", borderBottom: show ? "solid 1px var(--ui-border)" : undefined }}
      contentStyle={{ padding: show ? undefined : 0 }}
    >
      {!!show && <Overview />}
    </Card>
  );
};

export const Overview: FC = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const contentBoxSize = useResizeObserver(ref);
  const groupHeight = useGroupHeight();
  const panelWidth = useMemo(() => contentBoxSize?.width ?? 0, [contentBoxSize]);
  const refreshTracks = useGenomeViewerStore((state) => state.refreshTracks);
  const isDraggingRegionIndicator = useGenomeViewerStore((state) => state.isDraggingRegionIndicator);
  const moveCanvasScrollX = useGenomeViewerStore((state) => state.moveCanvasScrollX);
  const panelHeight = useGenomeViewerStore((state) =>
    getPanelHeight(
      [...state.trackGroups.values()].filter((trackGroup) => trackGroup.graphics.isVisible).length,
      groupHeight,
    ),
  );

  const maxWidth = useGenomeViewerStore((state) => state.getMaxWidth()[1]);
  const scale = getScale(panelWidth, maxWidth);

  const handlePointerDown = useCallback(
    (event: PointerEvent) => {
      event.preventDefault();
      moveCanvasScrollX(event.offsetX, scale);
      refreshTracks();
    },
    [moveCanvasScrollX, refreshTracks, scale],
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      event.preventDefault();

      if (isDraggingRegionIndicator && panelWidth > 0) {
        moveCanvasScrollX(event.offsetX, scale);
        refreshTracks();
      }
    },
    [isDraggingRegionIndicator, moveCanvasScrollX, panelWidth, refreshTracks, scale],
  );

  const handlePointerUp = useCallback((event: PointerEvent) => {
    event.preventDefault();

    useGenomeViewerStore.setState({
      isDraggingRegionIndicator: false,
    });
  }, []);

  useEffect(() => {
    const div = ref.current;

    if (div) {
      div.addEventListener("pointerdown", handlePointerDown);
      div.addEventListener("pointermove", handlePointerMove);
      div.addEventListener("pointerup", handlePointerUp);
    }
    return () => {
      if (div) {
        div.removeEventListener("pointerdown", handlePointerDown);
        div.removeEventListener("pointermove", handlePointerMove);
        div.removeEventListener("pointerup", handlePointerUp);
      }
    };
  }, [handlePointerDown, handlePointerMove, handlePointerUp]);

  return (
    <div ref={ref}>
      {maxWidth > 0 && (
        <svg width={panelWidth} height={panelHeight} xmlns="http://www.w3.org/2000/svg">
          <TrackGroupOverview panelWidth={panelWidth} scale={scale} />
          <VisibleAreaBox panelHeight={panelHeight} panelWidth={panelWidth} scale={scale} />
        </svg>
      )}
    </div>
  );
};
